/**
 * Generates, entirely in code, the Excel workbook used for the bulk "add users
 * by system access credential" flow (the template users download, fill in, and
 * upload to add existing principals to a scope).
 *
 * The workbook has four sheets: Instructions, Example (styled guidance with
 * required/optional coloring), Data (the sheet the parser reads — headers on
 * ADD_USER_COLUMN_NAME_EXCEL_ROW, data below), and a hidden _DoNotDelete sheet
 * backing the User Type dropdown.
 *
 * Styling (palette + layout) is supplied via a config object (see
 * DEFAULT_TEMPLATE_STYLE) so the values live in one place and can be tuned or
 * shared with the bulk-onboard template later.
 */
import Excel, { Workbook } from "exceljs";
// @ts-ignore
import { TFunction } from "react-i18next";
import {
    ADD_USER_DATA_SHEET_NAME,
    ADD_USER_COLUMN_NAME_EXCEL_ROW,
    ADD_USER_DATA_START_EXCEL_ROW,
    ADD_USER_DATA_END_EXCEL_ROW,
    ONBOARDABLE_USER_TYPES,
    DO_NOT_DELETE_EXCEL_SHEET_PROTECTION_STRING,
    EXCEL_TEMPLATE_COLORS,
} from "src/common/constants";
import { EXPECTED_HEADERS } from "src/pages/principals/system-access-credential-sheet-parser";

const REFERENCE_SHEET_NAME = "_DoNotDelete";

// Styling + layout config for the template workbook. Centralizes the palette and
// the Example-sheet row heights / column widths so the values live in one place
// (rather than scattered magic numbers) and can be tuned or shared with the
// bulk-onboard template later.
export interface TemplateStyle {
    colors: {
        blackFill: string;
        titleFont: string;
        sectionFill: string;
        headerFill: string;
        requiredFill: string;
        optionalFill: string;
    };
    // Example-sheet row heights, keyed by their role (not magic row numbers).
    rowHeights: {
        title: number; // row 1
        section: number; // row 2
        header: number; // row 3
        exampleData: number; // rows 4-5
        requirements: number; // row 6
        requiredLabels: number; // row 7
        // description row (row 8) height is COMPUTED from content; not fixed here
    };
    columnWidths: number[]; // per Data/Example column, e.g. [30, 30, 40]
}

export const DEFAULT_TEMPLATE_STYLE: TemplateStyle = {
    colors: {
        blackFill: EXCEL_TEMPLATE_COLORS.BLACK_FILL,
        titleFont: EXCEL_TEMPLATE_COLORS.TITLE_FONT,
        sectionFill: EXCEL_TEMPLATE_COLORS.SECTION_FILL,
        headerFill: EXCEL_TEMPLATE_COLORS.HEADER_FILL,
        requiredFill: EXCEL_TEMPLATE_COLORS.REQUIRED_FILL,
        optionalFill: EXCEL_TEMPLATE_COLORS.OPTIONAL_FILL,
    },
    rowHeights: { title: 24, section: 20, header: 22, exampleData: 18, requirements: 20, requiredLabels: 20 },
    columnWidths: [30, 30, 40],
};

// Builds a solid fill. For a solid pattern Excel renders the VISIBLE color from
// fgColor, so set both fg and bg to the same value.
const solidFill = (argb: string): Excel.Fill => ({
    type: "pattern",
    pattern: "solid",
    fgColor: { argb },
    bgColor: { argb },
});

// Builds the bulk-add template workbook in code. Sheet order (matches onboard):
//  - Instructions: human guidance (translated).
//  - Example: each column with a Required/Optional label and short example.
//  - Data: the three stable headers + a User Type dropdown on the data rows.
//  - _DoNotDelete (hidden): the User Type option list the dropdown references.
//
// CONTRACT: the Data sheet's header MUST stay on row 1 (ADD_USER_COLUMN_NAME_EXCEL_ROW)
// with EXPECTED_HEADERS in columns 1-3. All "pretty" content lives on the
// Instructions and Example sheets so the parser keeps working.
export const buildTemplateWorkbook = (t: TFunction, style: TemplateStyle = DEFAULT_TEMPLATE_STYLE): Workbook => {
    const workbook = new Excel.Workbook();

    // Describes the 3 fields for the Example sheet.
    const exampleFields = [
        { header: EXPECTED_HEADERS[0], requiredLabel: t("required") as string, example: t("add-user-example-credential") as string },
        { header: EXPECTED_HEADERS[1], requiredLabel: t("required") as string, example: t("add-user-example-user-type") as string },
        { header: EXPECTED_HEADERS[2], requiredLabel: t("required-or-optional") as string, example: t("add-user-example-email") as string },
    ];

    // Instructions sheet
    const instructions = workbook.addWorksheet(t("instructions") as string);
    const instructionsTitle = instructions.getCell("A1");
    instructionsTitle.value = t("bulk-add-user-by-system-access-credential") as string;
    instructionsTitle.font = { bold: true };
    instructions.getCell("A2").value = t("bulk-add-user-instructions-step-2", {
        maxAddUserCount: ADD_USER_DATA_END_EXCEL_ROW - ADD_USER_DATA_START_EXCEL_ROW,
    }) as string;
    instructions.getCell("A3").value = t("bulk-add-user-instructions-step-3") as string;
    instructions.getCell("A4").value = t("add-user-see-example-tab", { sheetName: t("example") }) as string;

    // Example sheet — styled to match the onboard template (black title, blue
    // section band, charcoal column headers, pink/green required-ness fills,
    // wrapped descriptions). 3 columns (A-C).
    const example = workbook.addWorksheet(t("example") as string);
    const requiredOrOptionalLabel = t("required-or-optional") as string;
    const centerBold = { vertical: "middle" as const, horizontal: "center" as const, wrapText: true };
    const lastColLetter = String.fromCharCode("A".charCodeAt(0) + EXPECTED_HEADERS.length - 1);

    // Row 1 (title): merged black band, white bold, centered.
    example.mergeCells(`A1:${lastColLetter}1`);
    const exampleTitle = example.getCell("A1");
    exampleTitle.value = t("bulk-add-user-by-system-access-credential") as string;
    exampleTitle.fill = solidFill(style.colors.blackFill);
    exampleTitle.font = { bold: true, color: { argb: style.colors.titleFont } };
    exampleTitle.alignment = centerBold;

    // Row 2 (section band): merged blue band, white bold, centered.
    example.mergeCells(`A2:${lastColLetter}2`);
    const sectionCell = example.getCell("A2");
    sectionCell.value = t("general") as string;
    sectionCell.fill = solidFill(style.colors.sectionFill);
    sectionCell.font = { bold: true, color: { argb: style.colors.titleFont } };
    sectionCell.alignment = centerBold;

    // Row 3 (column headers): charcoal fill, white bold, centered.
    // Rows 4-5 (example values): two realistic sample rows.
    const exampleValueRows = [
        ["jdoe", "ASSOCIATE", "jdoe@amazon.com"],
        ["asmith", "USER_ADMIN_PRIMARY", "asmith@amazon.com"],
    ];
    exampleFields.forEach((field, i) => {
        const col = i + 1;
        const headerCell = example.getCell(3, col);
        headerCell.value = field.header;
        headerCell.fill = solidFill(style.colors.headerFill);
        headerCell.font = { bold: true, color: { argb: style.colors.titleFont } };
        headerCell.alignment = centerBold;

        exampleValueRows.forEach((vals, r) => {
            const valueCell = example.getCell(4 + r, col);
            valueCell.value = vals[i];
            valueCell.alignment = { vertical: "top", wrapText: true };
        });

        // Row 7: required/optional label, pink fill = Required, green = Required/Optional.
        const isRequiredOrOptional = field.requiredLabel === requiredOrOptionalLabel;
        const labelCell = example.getCell(7, col);
        labelCell.value = field.requiredLabel;
        labelCell.fill = solidFill(isRequiredOrOptional ? style.colors.optionalFill : style.colors.requiredFill);
        labelCell.font = { bold: true };
        labelCell.alignment = centerBold;

        // Row 8: example description, wrapped + top-aligned.
        const descCell = example.getCell(8, col);
        descCell.value = field.example;
        descCell.alignment = { vertical: "top", wrapText: true };
    });

    // Row 6 (requirements separator): merged charcoal band, white bold.
    example.mergeCells(`A6:${lastColLetter}6`);
    const requirementsCell = example.getCell("A6");
    requirementsCell.value = t("add-user-example-requirements-note") as string;
    requirementsCell.fill = solidFill(style.colors.headerFill);
    requirementsCell.font = { bold: true, color: { argb: style.colors.titleFont } };
    requirementsCell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };

    // Column widths wide enough to read the wrapped descriptions. Email/description
    // column benefits from extra width.
    EXPECTED_HEADERS.forEach((_, i) => {
        example.getColumn(i + 1).width = style.columnWidths[i];
    });

    // Row heights. exceljs cannot auto-size wrapped rows, so estimate: pick the
    // longest content in the row and budget ~line-height per wrapped line given
    // the column width. Title/section/header rows get a modest fixed height.
    example.getRow(1).height = style.rowHeights.title;
    example.getRow(2).height = style.rowHeights.section;
    example.getRow(3).height = style.rowHeights.header;
    example.getRow(4).height = style.rowHeights.exampleData;
    example.getRow(5).height = style.rowHeights.exampleData;
    example.getRow(6).height = style.rowHeights.requirements;
    example.getRow(7).height = style.rowHeights.requiredLabels;
    // Description row: size to fit the longest wrapped description at its column width.
    const descRowHeight = Math.max(
        ...exampleFields.map((field, i) => {
            const charsPerLine = Math.max(1, Math.floor(style.columnWidths[i]));
            const lines = Math.ceil(field.example.length / charsPerLine);
            return Math.max(1, lines) * 15 + 6;
        }),
    );
    example.getRow(8).height = descRowHeight;

    // Data sheet. Mirrors the onboard Data layout: row 1 black title band, row 2
    // blue section band, row 3 charcoal column headers, rows 4+ data. The parser
    // reads the header from ADD_USER_COLUMN_NAME_EXCEL_ROW (=3) and skips the
    // rows above it, so the title/section bands don't affect parsing.
    const data = workbook.addWorksheet(ADD_USER_DATA_SHEET_NAME);
    const dataLastColLetter = String.fromCharCode("A".charCodeAt(0) + EXPECTED_HEADERS.length - 1);

    // Row 1 (title): merged black band, white bold, centered.
    data.mergeCells(`A1:${dataLastColLetter}1`);
    const dataTitle = data.getCell("A1");
    dataTitle.value = t("bulk-add-user-by-system-access-credential") as string;
    dataTitle.fill = solidFill(style.colors.blackFill);
    dataTitle.font = { bold: true, color: { argb: style.colors.titleFont } };
    dataTitle.alignment = { vertical: "middle", horizontal: "center", wrapText: true };

    // Row 2 (section band): merged blue band, white bold, centered.
    data.mergeCells(`A2:${dataLastColLetter}2`);
    const dataSection = data.getCell("A2");
    dataSection.value = t("general") as string;
    dataSection.fill = solidFill(style.colors.sectionFill);
    dataSection.font = { bold: true, color: { argb: style.colors.titleFont } };
    dataSection.alignment = { vertical: "middle", horizontal: "center", wrapText: true };

    // Row 3 (column headers): charcoal fill + white bold, matching the Example sheet.
    // The header VALUES at this row, columns 1-3, are the parser's contract.
    const headerRow = data.getRow(ADD_USER_COLUMN_NAME_EXCEL_ROW);
    EXPECTED_HEADERS.forEach((header, i) => {
        const headerCell = headerRow.getCell(i + 1);
        headerCell.value = header;
        headerCell.fill = solidFill(style.colors.headerFill);
        headerCell.font = { bold: true, color: { argb: style.colors.titleFont } };
        headerCell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    });

    // Set sensible column widths (exceljs has no true autofit). Same 3 columns as
    // the Example sheet, so reuse the shared style.columnWidths.
    EXPECTED_HEADERS.forEach((_, i) => {
        data.getColumn(i + 1).width = style.columnWidths[i];
    });

    // Hidden reference sheet with the User Type option list (added last to keep
    // sheet order: Instructions, Example, Data, _DoNotDelete).
    const reference = workbook.addWorksheet(REFERENCE_SHEET_NAME);
    ONBOARDABLE_USER_TYPES.forEach((userType, idx) => {
        reference.getCell(`A${idx + 1}`).value = userType;
    });
    reference.state = "hidden";
    reference.protect(DO_NOT_DELETE_EXCEL_SHEET_PROTECTION_STRING, {});
    const userTypeListFormula = `=${REFERENCE_SHEET_NAME}!$A$1:$A$${ONBOARDABLE_USER_TYPES.length}`;

    // Add a User Type dropdown (column 2) to each data row (rows 2..101).
    for (let r = ADD_USER_DATA_START_EXCEL_ROW; r < ADD_USER_DATA_END_EXCEL_ROW; r++) {
        data.getCell(r, 2).dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: [userTypeListFormula],
            showErrorMessage: true,
            errorTitle: t("select-valid-option") as string,
            error: t("excel-dropdown-entry-error") as string,
        };
    }

    // Black-fill row across the 3 columns just past the last data row, marking
    // where the import stops (mirrors the onboard template). For a solid pattern,
    // Excel renders the visible color from fgColor, so set both fg and bg.
    for (let c = 1; c <= EXPECTED_HEADERS.length; c++) {
        data.getCell(ADD_USER_DATA_END_EXCEL_ROW, c).fill = solidFill(style.colors.blackFill);
    }

    return workbook;
};
